import {Box, Button, CircularProgress, IconButton, Stack} from '@mui/material';
import 'cropperjs/dist/cropper.css';
import {useRef, useState} from 'react';
import Cropper from 'react-cropper';
import {toast} from 'react-toastify';
import BasicModal from 'src/components/modals/BasicModal';
import {uploadFileRequest} from 'src/utils/AdminApi';
import DragDropInput from './DragDropInput';
import {validateFileSize, validateFileType} from './filesUtils';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const SimpleFileUploadField = ({
	value,
	onChange,
	label,
	defaultImage = null,
	onClear = () => {},
	ratio = 4 / 5,
	helperText,
	maxSize = 2,
	error,
	placeholder,
	renderInput,
	previewStyles = {},
}) => {
	// this will be used to trigger upload files when button clicked
	const fileInputRef = useRef(null);
	// image source ( data url ) for preview, example preview image in update page
	const [selectedImage, setSelectedImage] = useState();
	const [image, setImage] = useState(defaultImage);
	//####################################
	// cropper
	const cropperRef = useRef(null);
	const [shouldShowCropModal, setShouldShowCropModal] = useState(false);
	// used to show loader until upload image to server complete
	const [isUploading, setIsUploading] = useState(false);
	//####################################

	const handleFileUpload = (e) => {
		e.preventDefault();
		let files;
		if (e.dataTransfer) {
			files = e.dataTransfer.files;
		} else if (e.target) {
			files = e.target.files;
		}
		// validation
		if (!files || files.length === 0) return;

		if (!validateFileType(files[0])) {
			return toast.error('ملف غير مدعوم');
		}
		if (!validateFileSize(files[0], maxSize)) {
			return toast.error(`حجم الصور يجب ان لا يتجاوز ${maxSize} ميجا`);
		}

		setSelectedImage(URL.createObjectURL(files[0]));
		setShouldShowCropModal(true);
	};

	const confirmFileCrop = () => {
		const croppedCanves = cropperRef.current.cropper.getCroppedCanvas();
		// disabled because of performance issue due ui thread blocking, readImageFromFile used instead
		// setImage(croppedCanves.toDataURL('image/webp', 0.9));
		setIsUploading(true);
		croppedCanves.toBlob(
			async (blob) => {
				try {
					const res = await uploadFileRequest(blob);
					setImage(res[0].url);
					setShouldShowCropModal(false);
					onChange(res[0].id, res[0]);
				} catch (err) {
					toast.error('تعذر تحميل الصورة');
				} finally {
					setIsUploading(false);
				}
			},
			'image/webp',
			0.9,
		);
	};

	// eslint-disable-next-line no-unused-vars
	const clearImage = () => {
		setImage(null);
		onChange(null);
		onClear();
	};

	return (
		<div>
			<Stack direction="column" alignItems="center">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 50,
						height: 50,
						border: '1px solid #ccc',
						borderRadius: 2,
						position: 'relative',
						overflow: 'hidden',
					}}
				>
					<input
						ref={fileInputRef}
						type="file"
						accept="*"
						style={{
							display: 'none', // Hide the input completely
						}}
						onChange={handleFileUpload}
					/>
					{!value ? (
						<IconButton
							component="span"
							sx={{zIndex: 1}}
							onClick={() => fileInputRef.current && fileInputRef.current.click()}
						>
							<CameraAltIcon fontSize="small" />
						</IconButton>
					) : (
						<>
							<img src={image} style={{width: '100%', height: '100%'}} alt="الصورة المراد تحميلها" />
						</>
					)}
				</Box>
				{value && (
					<button
						type="button"
						style={{
							fontSize: '10px',
							p: 0,
							m: 0,
							background: 'none',
							border: 'none',
							color: '#067E6E',
							cursor: 'pointer',
						}}
						onClick={() => fileInputRef.current && fileInputRef.current.click()}
					>
						تغيير
					</button>
				)}
			</Stack>

			{error && <p style={{color: 'red'}}>{helperText}</p>}

			{/* crop modal */}
			<BasicModal
				isVisible={shouldShowCropModal}
				// prevent dismiss
				setVisibility={setShouldShowCropModal}
			>
				<Cropper
					movable={false}
					draggable={false}
					modal={false}
					style={{
						height: 200,
						width: '100%',
					}}
					initialAspectRatio={ratio}
					aspectRatio={ratio}
					src={selectedImage ?? image}
					zoomable={false}
					viewMode={1}
					minCropBoxHeight={10}
					minCropBoxWidth={10}
					background={false}
					// responsive={true}
					autoCropArea={1}
					checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
					ref={cropperRef}
					guides
				/>

				<p style={{textAlign: 'center'}}>يرجي تحديد ابعاد الصورة لكي تظهر بشكل مناسب في الموقع</p>

				{isUploading ? (
					<CircularProgress />
				) : (
					<Button
						fullWidth
						size="large"
						sx={{mt: 2}}
						variant="contained"
						type="button"
						onClick={confirmFileCrop}
					>
						اختيار
					</Button>
				)}
			</BasicModal>
		</div>
	);
};

export default SimpleFileUploadField;
